import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { Eye, Check, X, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';

interface Suggestion {
    id: number;
    recap_id: number;
    user_id: number;
    suggested_series_name: string;
    status: 'pending' | 'approved' | 'rejected';
    created_at: string;
    updated_at: string;
    admin_notes?: string;
    username: string;
    recap_title: string;
    thumbnail_url: string;
}

const AdminSuggestionsPage: React.FC = () => {
    const { user } = useAuth();
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
    const [adminNotes, setAdminNotes] = useState('');

    useEffect(() => {
        if (user?.is_admin) {
            fetchPendingSuggestions();
        }
    }, [user]);

    const fetchPendingSuggestions = async () => {
        try {
            const response = await fetch('/api/suggestions/admin/pending', {
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setSuggestions(data);
            } else if (response.status === 403) {
                toast.error('Admin access required');
            } else if (response.status === 401) {
                toast.error('Please log in to access admin features');
            } else {
                const errorData = await response.json().catch(() => ({}));
                toast.error(errorData.error || 'Failed to fetch suggestions');
            }
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            toast.error('Network error - please check your connection');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (suggestion: Suggestion) => {
        try {
            const response = await fetch(`/api/suggestions/admin/${suggestion.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    status: 'approved',
                    admin_notes: adminNotes
                })
            });

            if (response.ok) {
                toast.success('Suggestion approved!');
                setSuggestions(suggestions.filter(s => s.id !== suggestion.id));
                setSelectedSuggestion(null);
                setAdminNotes('');
            } else {
                toast.error('Failed to approve suggestion');
            }
        } catch (error) {
            console.error('Error approving suggestion:', error);
            toast.error('Network error');
        }
    };

    const handleReject = async (suggestion: Suggestion) => {
        try {
            const response = await fetch(`/api/suggestions/admin/${suggestion.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    status: 'rejected',
                    admin_notes: adminNotes
                })
            });

            if (response.ok) {
                toast.success('Suggestion rejected');
                setSuggestions(suggestions.filter(s => s.id !== suggestion.id));
                setSelectedSuggestion(null);
                setAdminNotes('');
            } else {
                toast.error('Failed to reject suggestion');
            }
        } catch (error) {
            console.error('Error rejecting suggestion:', error);
            toast.error('Network error');
        }
    };

    if (!user?.is_admin) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        Access Denied
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        You need admin privileges to access this page.
                    </p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading suggestions...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
            <div className="max-w-6xl mx-auto px-4">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        Series Name Suggestions
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Review and manage user suggestions for series names
                    </p>
                </div>

                {suggestions.length === 0 ? (
                    <div className="text-center py-12">
                        <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            No pending suggestions
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                            All suggestions have been reviewed.
                        </p>
                    </div>
                ) : (
                    <div className="grid gap-6">
                        {suggestions.map((suggestion) => (
                            <div
                                key={suggestion.id}
                                className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
                            >
                                <div className="flex items-start space-x-4">
                                    <img
                                        src={suggestion.thumbnail_url}
                                        alt={suggestion.recap_title}
                                        className="w-24 h-16 object-cover rounded"
                                    />
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                                            {suggestion.recap_title}
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                            Suggested by: <span className="font-medium">{suggestion.username}</span>
                                        </p>
                                        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded border border-purple-200 dark:border-purple-800">
                                            <p className="text-sm text-purple-800 dark:text-purple-200">
                                                <span className="font-medium">Suggested series name:</span> {suggestion.suggested_series_name}
                                            </p>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">
                                            Submitted: {new Date(suggestion.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <div className="flex flex-col space-y-2">
                                        <button
                                            onClick={() => setSelectedSuggestion(suggestion)}
                                            className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                            title="Review suggestion"
                                        >
                                            <Eye className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Review Modal */}
                {selectedSuggestion && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                Review Suggestion
                            </h3>

                            <div className="mb-4">
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    <strong>Recap:</strong> {selectedSuggestion.recap_title}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    <strong>Suggested by:</strong> {selectedSuggestion.username}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    <strong>Suggested name:</strong> {selectedSuggestion.suggested_series_name}
                                </p>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Admin Notes (optional)
                                </label>
                                <textarea
                                    value={adminNotes}
                                    onChange={(e) => setAdminNotes(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                                    rows={3}
                                    placeholder="Add notes about this suggestion..."
                                />
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => {
                                        setSelectedSuggestion(null);
                                        setAdminNotes('');
                                    }}
                                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleReject(selectedSuggestion)}
                                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center space-x-1"
                                >
                                    <X className="h-4 w-4" />
                                    <span>Reject</span>
                                </button>
                                <button
                                    onClick={() => handleApprove(selectedSuggestion)}
                                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center space-x-1"
                                >
                                    <Check className="h-4 w-4" />
                                    <span>Approve</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminSuggestionsPage; 